import { useAtomValue } from 'jotai'
import { nanoid } from 'nanoid'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { compile } from '~/apps/ninesegsAnalyzer/routes/paths'
import { pathKey } from '~/apps/ninesegsAnalyzer/utils/customerAnalysis'
import { brandGroupIdAtom } from '~/atoms'
import { useCreateSurveyFilterRestoringParams } from '~/hooks/useCreateSurveyFilterRestoringParams'
import { useSurveyQueryData } from '~/hooks/useSurveyQuery'
import { assertExists } from '~/utils'

import {
  dataDisplayTypeAtom,
  fiveSegmentCommentAtom,
  fiveSegmentHiddenColumnKeysAtom,
  fiveSegmentSortValueAtom,
  hiddenRowIdsAtom,
  nineSegmentCommentAtom,
  nineSegmentHiddenColumnKeysAtom,
  nineSegmentSortValueAtom,
  segmentDisplayTypeAtom,
} from '../../atoms'

import { type RestoringParams } from './types'

export const useRestoringParams = (hash: RestoringParams['hash']) => {
  const { t } = useTranslation()
  const createSurveyFilterRestoringParams =
    useCreateSurveyFilterRestoringParams()

  const survey = useSurveyQueryData()

  const brandGroupId = useAtomValue(brandGroupIdAtom)

  const dataDisplayType = useAtomValue(dataDisplayTypeAtom)
  const segmentDisplayType = useAtomValue(segmentDisplayTypeAtom)
  const fiveSegmentComment = useAtomValue(fiveSegmentCommentAtom)

  const fiveSegmentSortValue = useAtomValue(fiveSegmentSortValueAtom)
  const nineSegmentComment = useAtomValue(nineSegmentCommentAtom)
  const nineSegmentSortValue = useAtomValue(nineSegmentSortValueAtom)

  const hiddenRowIds = useAtomValue(hiddenRowIdsAtom)

  const fiveSegmentHiddenColumnKeys = useAtomValue(
    fiveSegmentHiddenColumnKeysAtom,
  )
  const nineSegmentHiddenColumnKeys = useAtomValue(
    nineSegmentHiddenColumnKeysAtom,
  )

  return React.useMemo(() => {
    assertExists(survey, t('assertion_failed'))
    assertExists(brandGroupId, t('assertion_failed'))

    const hasComment =
      (hash === '5segs' ? fiveSegmentComment : nineSegmentComment) !== ''

    const params = createSurveyFilterRestoringParams<RestoringParams>(
      compile(pathKey, { surveyId: survey.id }),
      {
        version: 2,
        revision: 3,
        brandGroupId,
        dataDisplayType,
        segmentDisplayType,
        fiveSegmentComment,
        fiveSegmentSortValue,
        nineSegmentComment,
        nineSegmentSortValue,
        hiddenRowIds,
        fiveSegmentHiddenColumnKeys,
        nineSegmentHiddenColumnKeys,
        hash,
      },
    )

    // Attach a unique commentRef only when this figure has a comment, so the saved
    // figure maps 1:1 to its DocumentImage. No comment → no marker, so comment-less
    // shares keep content-dedup.
    return hasComment ? { ...params, commentRef: nanoid() } : params
  }, [
    brandGroupId,
    createSurveyFilterRestoringParams,
    dataDisplayType,
    fiveSegmentComment,
    fiveSegmentHiddenColumnKeys,
    fiveSegmentSortValue,
    hash,
    hiddenRowIds,
    nineSegmentComment,
    nineSegmentHiddenColumnKeys,
    nineSegmentSortValue,
    segmentDisplayType,
    survey,
    t,
  ])
}
